import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, CheckCircle, MapPin, Globe, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

const API = "/api";

const VENUE_TYPES = [
  "Music Venue", "Bar/Club", "Theater", "Concert Hall", "Restaurant", 
  "Brewery/Taproom", "Coffee Shop", "Art Gallery", "Outdoor Space",
  "Community Center", "Church/Religious", "Other"
];

const NEIGHBORHOODS_MPLS = [
  "Downtown", "North Loop", "Northeast", "Uptown", "Lyn-Lake",
  "Dinkytown", "Cedar-Riverside", "Warehouse District", "Loring Park",
  "Whittier", "Powderhorn", "Longfellow", "South Minneapolis", "Other"
];

const NEIGHBORHOODS_STP = [
  "Downtown", "Lowertown", "West 7th", "Grand Avenue", "Summit Hill",
  "Highland Park", "Como", "Midway", "Cathedral Hill", "Other"
];

const RegisterVenue = () => {
  const [submitted, setSubmitted] = useState(false);
  const [registrationId, setRegistrationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    venue_name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    city: 'Minneapolis',
    neighborhood: '',
    capacity: '',
    venue_type: '',
    description: '',
    amenities: '',
    website: '',
    instagram: '',
    facebook: '',
    ticket_url: '',
    photos: [''],
    booking_email: '',
    booking_info: '',
    typical_genres: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const cleanForm = {
        ...form,
        capacity: form.capacity ? parseInt(form.capacity) : null,
        photos: form.photos.filter(p => p.trim())
      };
      
      const response = await axios.post(`${API}/register/venue`, cleanForm);
      setRegistrationId(response.data.id);
      setSubmitted(true);
      toast.success('Registration submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit registration');
    } finally {
      setLoading(false);
    }
  };

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const updateArrayField = (field, index, value) => {
    setForm(prev => {
      const arr = [...prev[field]];
      arr[index] = value;
      return { ...prev, [field]: arr };
    });
  };

  const addArrayField = (field) => {
    setForm(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const neighborhoods = form.city === 'Minneapolis' ? NEIGHBORHOODS_MPLS : NEIGHBORHOODS_STP;

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="venue-registration-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md space-y-6"
        >
          <div className="w-20 h-20 mx-auto rounded-full bg-secondary/20 flex items-center justify-center">
            <CheckCircle size={40} className="text-secondary" />
          </div>
          <h1 className="font-heading text-3xl font-bold">Registration Submitted!</h1>
          <p className="text-muted-foreground">
            Thank you for registering your venue with Twin Tube! We'll review your submission and get back to you within 3-5 business days.
          </p>
          <div className="p-4 bg-card rounded-sm border border-border">
            <p className="text-sm text-muted-foreground mb-1">Your Reference ID:</p>
            <code className="text-primary font-mono text-sm">{registrationId}</code>
          </div>
          <p className="text-sm text-muted-foreground">
            Once approved, your venue will get its own landing page on Twin Tube where fans can find your upcoming events, photos, and info — plus you'll be able to submit events directly!
          </p>
          <div className="flex gap-3 justify-center">
            <Link to="/">
              <Button variant="outline">Back to Home</Button>
            </Link>
            <Link to="/register/event">
              <Button>Submit an Event</Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="register-venue-page">
      {/* Back Link */}
      <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft size={16} />
        Back to Home
      </Link>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-4"
      >
        <div className="w-16 h-16 mx-auto rounded-full bg-secondary/20 flex items-center justify-center">
          <Building2 size={32} className="text-secondary" />
        </div>
        
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
          Venue Registry
        </h1>
        <p className="text-muted-foreground max-w-xl mx-auto">
          Add your venue to the Twin Cities music scene on Twin Tube. Promote your shows, 
          connect with local artists, and reach music lovers across Minneapolis and St. Paul.
        </p>
      </motion.div>

      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading">Venue Information</CardTitle>
              <CardDescription>Tell us about your venue</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Venue Name *</Label>
                  <Input
                    value={form.venue_name}
                    onChange={(e) => updateForm('venue_name', e.target.value)}
                    placeholder="Your venue name"
                    required
                    data-testid="venue-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Venue Type *</Label>
                  <Select value={form.venue_type} onValueChange={(v) => updateForm('venue_type', v)} required>
                    <SelectTrigger data-testid="venue-type-select">
                      <SelectValue placeholder="Select venue type" />
                    </SelectTrigger>
                    <SelectContent>
                      {VENUE_TYPES.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => updateForm('description', e.target.value)}
                  placeholder="Tell us about your venue - its history, vibe, what makes it special..."
                  rows={4}
                  required
                  data-testid="description-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Capacity</Label>
                  <Input
                    type="number"
                    value={form.capacity}
                    onChange={(e) => updateForm('capacity', e.target.value)}
                    placeholder="e.g., 250"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Typical Genres Booked</Label>
                  <Input
                    value={form.typical_genres}
                    onChange={(e) => updateForm('typical_genres', e.target.value)}
                    placeholder="e.g., Rock, Jazz, Hip Hop"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Amenities</Label>
                <Textarea
                  value={form.amenities}
                  onChange={(e) => updateForm('amenities', e.target.value)}
                  placeholder="PA system, green room, parking, food service, etc."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Location */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <MapPin size={20} /> Location
              </CardTitle>
              <CardDescription>Physical address is required for verification</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Physical Address *</Label>
                <Input
                  value={form.address}
                  onChange={(e) => updateForm('address', e.target.value)}
                  placeholder="123 Main Street"
                  required
                  data-testid="address-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>City *</Label>
                  <Select value={form.city} onValueChange={(v) => { updateForm('city', v); updateForm('neighborhood', ''); }}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Minneapolis">Minneapolis</SelectItem>
                      <SelectItem value="St. Paul">St. Paul</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Neighborhood</Label>
                  <Select value={form.neighborhood} onValueChange={(v) => updateForm('neighborhood', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select neighborhood" />
                    </SelectTrigger>
                    <SelectContent>
                      {neighborhoods.map(n => (
                        <SelectItem key={n} value={n}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading">Contact Information</CardTitle>
              <CardDescription>How can we and artists reach you?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Name *</Label>
                  <Input
                    value={form.contact_name}
                    onChange={(e) => updateForm('contact_name', e.target.value)}
                    placeholder="Primary contact person"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => updateForm('email', e.target.value)}
                    placeholder="venue@email.com"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone *</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => updateForm('phone', e.target.value)}
                    placeholder="(612) 555-0123"
                    required
                    data-testid="phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Booking Email</Label>
                  <Input
                    value={form.booking_email}
                    onChange={(e) => updateForm('booking_email', e.target.value)}
                    placeholder="booking@venue.com"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Booking Information</Label>
                <Textarea
                  value={form.booking_info}
                  onChange={(e) => updateForm('booking_info', e.target.value)}
                  placeholder="How should artists reach out? Any requirements for booking?"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Online Presence */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Globe size={20} /> Online Presence
              </CardTitle>
              <CardDescription>Your website and social media</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={form.website}
                    onChange={(e) => updateForm('website', e.target.value)}
                    placeholder="https://yourvenue.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ticket/Event URL</Label>
                  <Input
                    value={form.ticket_url}
                    onChange={(e) => updateForm('ticket_url', e.target.value)}
                    placeholder="https://... (Eventbrite, etc.)"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Instagram</Label>
                  <Input
                    value={form.instagram}
                    onChange={(e) => updateForm('instagram', e.target.value)}
                    placeholder="@yourvenue"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Facebook</Label>
                  <Input
                    value={form.facebook}
                    onChange={(e) => updateForm('facebook', e.target.value)}
                    placeholder="https://facebook.com/yourvenue"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Photos */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading">Venue Photos</CardTitle>
              <CardDescription>Share photos of your venue (first one will be the main image)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {form.photos.map((photo, i) => (
                <Input
                  key={i}
                  value={photo}
                  onChange={(e) => updateArrayField('photos', i, e.target.value)}
                  placeholder="https://... (direct image URL)"
                />
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => addArrayField('photos')}>
                + Add Another Photo
              </Button>
            </CardContent>
          </Card>

          {/* Submit */}
          <Button 
            type="submit" 
            size="lg" 
            className="w-full gap-2"
            disabled={loading}
            data-testid="submit-btn"
          >
            {loading ? 'Submitting...' : 'Submit Registration'}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            By submitting, you confirm that you are authorized to represent this venue.
            We typically review submissions within 3-5 business days.
          </p>
        </form>
      </motion.div>
    </div>
  );
};

export default RegisterVenue;
